<script lang="ts">
  import { app } from '../../lib/app.svelte';
  import { fmtTime } from '../../lib/format';
  const m = $derived(app.meta);
  const s = $derived(app.stale);
  const rpYear = $derived(app.meta?.dataset?.year ?? app.replay);
</script>

{#if app.replay}
  <div class="rpstrip" role="note" aria-label="Replay mode">
    <div class="wrap rpin">
      <span class="rptag">Replay</span>
      <span class="rptext" title={app.meta?.election.name ?? ''}><b>{rpYear} general election</b><span class="rpmore">{' · '}real {rpYear} results, simulated reporting order</span>{' · '}<b>not 2026</b></span>
      <button class="rpback" type="button" onclick={() => app.switchDataset(null)}>Back to 2026 live</button>
    </div>
  </div>
{/if}
<div class="wrap banners">
  {#if m?.notice && !app.replay}
    <div class="bn info notice" role="note"><span class="ic" aria-hidden="true">i</span><span>{m.notice.text}{#if m.notice.link}{' '}<a href={m.notice.link.href} target="_blank" rel="noopener">{m.notice.link.label}</a>{/if}</span></div>
  {/if}
  <!-- before polls close (live 2026) nothing can be out of date yet: no stale / feed-health / connection warnings -->
  {#if app.loadFailed && app.shownSid && app.shownSid !== app.viewSid && !app.preClose}
    <div class="bn warn" role="status"><span class="ic" aria-hidden="true">!</span><span><b>Couldn’t load the latest update.</b> Showing results as of {fmtTime(app.shownTime)}. Retrying…</span></div>
  {/if}
  {#if !app.replay && !app.isLive && app.viewTime !== null}
    <div class="bn replay" role="status">
      <span class="ic" aria-hidden="true"><svg width="16" height="16" viewBox="0 0 24 24"><path d="M3 12a9 9 0 1 0 3-6.7M3 4v5h5" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" /></svg></span>
      <span><b>Viewing {fmtTime(app.viewTime)}</b> — results as they stood at that moment.</span>
      <button class="btn small primary" type="button" onclick={() => { app.playing = false; app.setAt(null); }}>Back to live</button>
    </div>
  {/if}
  {#if app.preClose}
    <!-- quiet until polls close -->
  {:else if s && m && !app.replay}
    <div class="bn bad" role="alert">
      <span class="ic" aria-hidden="true">!</span>
      {#if s.kind === 'feed'}
        <span><b>Results may be out of date — the state results feed hasn’t been reachable{s.since !== null ? ` since ${fmtTime(s.since)}` : ''}.</b>{m.ingestion_mode === 'manual' ? '' : ' Showing the last results received.'}</span>
      {:else}
        <span><b>Results may be out of date — last update {fmtTime(s.since)}.</b> {s.kind === 'fetch' ? 'We can’t reach the results server right now; you’re seeing the last numbers we received.' : 'The results feed hasn’t checked in recently.'}</span>
      {/if}
    </div>
  {:else if !m && app.metaFailures >= 3 && !app.replay}
    <div class="bn bad" role="alert"><span class="ic">!</span><span><b>Can’t reach the results server.</b> Retrying automatically.</span></div>
  {/if}
  {#if m && !m.static}
    {#if app.preClose}
      <!-- quiet until polls close -->
    {:else if m.health.status === 'red' && s?.kind !== 'feed'}<div class="bn bad"><span class="ic">!</span><span><b>Results feed problem.</b> {m.health.message}</span></div>
    {:else if m.health.status === 'amber' && m.ingestion_mode !== 'manual'}<div class="bn warn"><span class="ic">!</span><span><b>Results feed delayed.</b> {m.health.message}</span></div>{/if}
    {#if m.ingestion_mode === 'manual'}<div class="bn warn"><span class="ic">i</span><span><b>Manual updates:</b> the live feed is down, so the state’s official results files are being loaded by hand. Updates may lag the state’s results site.</span></div>{/if}
    {#if m.pinned}<div class="bn info"><span class="ic">i</span><span><b>Results are paused</b> at an earlier update while a data problem is checked.</span></div>{/if}
  {/if}
</div>

<style>
  .rpstrip { background: var(--rp); color: #1a1205; border-bottom: 1px solid color-mix(in oklab, var(--rp) 70%, #000); }
  .rpin { display: flex; align-items: center; gap: 12px; padding-top: 9px; padding-bottom: 9px; font-size: 0.9rem; }
  .rptag { flex: none; font: 800 0.7rem/1 var(--font); letter-spacing: 0.12em; text-transform: uppercase; background: #1a1205; color: var(--rp); padding: 5px 8px; border-radius: 6px; }
  .rptext { min-width: 0; }
  .rpback { margin-left: auto; flex: none; border: 1.5px solid #1a1205; background: transparent; color: #1a1205; font-weight: 700; font-size: 0.82rem; border-radius: 999px; padding: 6px 12px; cursor: pointer; white-space: nowrap; }
  .rpback:hover { background: #1a1205; color: var(--rp); }
  @media (max-width: 640px) { .rpin { font-size: 0.82rem; gap: 8px; align-items: flex-start; } .rpmore { display: none; } .rpback { padding: 5px 10px; font-size: 0.76rem; } }
  .banners { display: flex; flex-direction: column; gap: 8px; margin-top: 12px; }
  .banners:empty { display: none; }
  .bn { display: flex; align-items: center; gap: 10px; padding: 10px 14px; border-radius: 14px; font-size: 0.9rem; line-height: 1.35; }
  .bn .ic { flex: none; width: 24px; height: 24px; border-radius: 50%; display: grid; place-items: center; font-weight: 800; font-size: 0.85rem; background: color-mix(in oklab, currentColor 14%, transparent); }
  .bn .btn { margin-left: auto; }
  .bad { background: var(--banner-bad-bg); color: var(--banner-bad-ink); }
  .warn { background: var(--banner-warn-bg); color: var(--banner-warn-ink); }
  .info { background: var(--card-3); color: var(--ink-2); }
  .replay { background: var(--est-bg); color: var(--est-ink); border: 1px solid var(--est-line); }
  @media (max-width: 640px) { .bn { font-size: 0.84rem; align-items: flex-start; flex-wrap: wrap; } .bn .btn { margin-left: 34px; } }
</style>
